/**
 * One read for the whole Hour Optimizer window: the campaign's hours ranked
 * on the draft formula and rules (the live preview), the extra drops those
 * settings place — the planner's own function — and the notes of the extras
 * rules that change nothing today. The panel, the insight, the chart and the
 * rule cards all read this one answer.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { type DropGenHourOptimizerConfig, effectiveHourFormula } from '@trafficpulse/shared';

import { useHourProfile } from '../../../../../hooks/useHourProfile';
import type { CountryWindow } from '../hour-chart-math';
import { extrasSpan, previewExtras } from './extras-preview';
import { ruleNotes } from './rule-notes';

interface Args {
  countryKey: string;
  campaignWiserId: number | null;
  config: DropGenHourOptimizerConfig;
  startMin: number | null;
  endAtMin: number | null;
  slotMinutes: number[];
  window: CountryWindow | null;
}

export function useHourExtras(a: Args) {
  const profile = useHourProfile({ countryKey: a.countryKey, campaignWiserId: a.campaignWiserId, formula: effectiveHourFormula(a.config), rules: a.config.rules });
  const slotsKey = a.slotMinutes.join(',');
  const span = useMemo(
    () => extrasSpan(a.startMin, a.endAtMin, a.slotMinutes, a.window),
    // The minutes by value, not by array identity.
    [a.startMin, a.endAtMin, slotsKey, a.window?.startMin, a.window?.endMin],
  );
  const buckets = profile.data?.buckets;
  const { minIntervalMin, maxExtraSlots } = a.config;
  const preview = useMemo(() => (buckets && span ? previewExtras(buckets, span, { minIntervalMin, maxExtraSlots }, a.config.rules) : null), [buckets, span, minIntervalMin, maxExtraSlots, a.config.rules]);
  const notes = useMemo(() => (buckets && span ? ruleNotes(buckets, span, { minIntervalMin, maxExtraSlots }, a.config.rules ?? []) : {}), [buckets, span, minIntervalMin, maxExtraSlots, a.config.rules]);
  return { profile, span, preview, notes };
}

export type HourExtrasRead = ReturnType<typeof useHourExtras>;
